import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  InvitationForm,
  InvitationFormData,
} from "@/app/components/invitation/InvitationForm";
import {
  RenterRequisiteForm,
  RenterRequisiteFormData,
} from "@/app/components/invitation/RenterRequisiteForm";
import { InvitationReview } from "@/app/components/invitation/InvitationReview";
import { Stepper } from "@/app/components/initiate/Stepper";
import { ModalError } from "@/app/components/modals/modalBuilder";
import { InvitationLinkModal } from "@/app/components/modals/invitation/invitationLinkModal";
import { useDisclosure } from "@heroui/react";
import useErrorHandling from "@/hooks/useErrorHandling";
import { api } from "@/api";
import { InvitationCreateExisting } from "@/api/invitations";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  OfficesSelectionForm,
  OfficesSelectionFormData,
} from "@/app/components/invitation/OfficesSelectionForm";

export function CreateExistingInvitationPage() {
  const navigate = useNavigate();
  const handleError = useErrorHandling();
  const businessProfileId = useAppSelector(
    (state) => state.businessProfile.businessProfile.id,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [invitationData, setInvitationData] =
    useState<InvitationFormData | null>(null);
  const [officesData, setOfficesData] =
    useState<OfficesSelectionFormData | null>(null);
  const [createNewRequisite, setCreateNewRequisite] = useState(false);
  const [renterRequisiteData, setRenterRequisiteData] =
    useState<RenterRequisiteFormData | null>(null);
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationLink, setInvitationLink] = useState<string>("");
  const linkModalDisclosure = useDisclosure();
  const wasModalOpenRef = useRef(false);

  const steps = useMemo(() => {
    const baseSteps = [
      {
        name: "Данные приглашения",
        description: "Заполните основную информацию",
        longDescription: "Укажите арендатора и параметры договора",
      },
      {
        name: "Офисы",
        description: "Выберите офисы и рабочие места",
        longDescription: "Укажите офисы и рабочие места для договора",
      },
    ];

    if (createNewRequisite) {
      baseSteps.push({
        name: "Реквизиты арендатора",
        description: "Заполните реквизиты арендатора",
        longDescription: "Введите реквизиты нового арендатора",
      });
    }

    baseSteps.push({
      name: "Проверка данных",
      description: "Проверьте введенные данные",
      longDescription: "Убедитесь, что все данные корректны перед отправкой",
    });

    return baseSteps;
  }, [createNewRequisite]);

  const handleCreateRequisiteToggle = useCallback((value: boolean) => {
    setCreateNewRequisite(value);
    if (!value) {
      setRenterRequisiteData(null);
    }
  }, []);

  const handleInvitationSubmit = useCallback(
    (data: InvitationFormData) => {
      setInvitationData(data);
      setModalErrors([]);
      setActiveStep(1); // Переход к шагу выбора офисов
    },
    [],
  );

  const handleOfficesSubmit = useCallback(
    (data: OfficesSelectionFormData) => {
      setOfficesData(data);
      setModalErrors([]);
      if (createNewRequisite) {
        setActiveStep(2); // Переход к шагу реквизитов
      } else {
        setActiveStep(steps.length - 1); // Переход к проверке данных
      }
    },
    [createNewRequisite, steps.length],
  );

  const handleRenterRequisiteSubmit = useCallback(
    (data: RenterRequisiteFormData) => {
      setRenterRequisiteData(data);
      setModalErrors([]);
      setActiveStep(steps.length - 1); // Переход к проверке данных
    },
    [steps.length],
  );

  const handleReviewSubmit = useCallback(async () => {
    if (!invitationData || !invitationData.renter || !officesData) return;

    setIsSubmitting(true);

    try {
      // Разделяем офисы и рабочие места
      const offices = officesData.offices
        .filter((item) => item.office_id !== null && !item.workplace_id)
        .map((item) => ({
          office_id: item.office_id!,
          price: item.price,
        }));

      const workplaces = officesData.offices
        .filter((item) => item.workplace_id !== null)
        .map((item) => ({
          workplace_id: item.workplace_id!,
          price: item.price,
        }));

      const payload: InvitationCreateExisting = {
        business_profile_id: businessProfileId,
        start_date: invitationData.startDate,
        end_date: invitationData.endDate,
        meetingHours: invitationData.meetingHours,
        offices: offices,
        workplaces: workplaces,
        renter: invitationData.renter,
        requisite: invitationData.requisite,
        type: "existing_renter",
        number: invitationData.number,
      };

      if (createNewRequisite && renterRequisiteData) {
        payload.meta = {
          requisite_name: renterRequisiteData.requisite_name,
          address: renterRequisiteData.address,
          ogrn: renterRequisiteData.ogrn,
          inn: renterRequisiteData.inn,
          kpp: renterRequisiteData.kpp,
          bik: renterRequisiteData.bik,
          bank_account: renterRequisiteData.bank_account,
          correspondent_account: renterRequisiteData.correspondent_account,
          bank_name: renterRequisiteData.bank_name,
          phone_number: renterRequisiteData.phone_number,
          email: renterRequisiteData.email,
        };
      }

      const response = await api.invitations.create(payload);
      const link = `https://app.elasticspace.app/invitations/${response.data.opaque_id}`;
      setInvitationLink(link);
      setIsSubmitting(false);
      linkModalDisclosure.onOpen();
    } catch (err: any) {
      const { errorMessage, parsedErrors } = handleError(err);
      setModalErrors(parsedErrors || []);
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  }, [
    invitationData,
    officesData,
    createNewRequisite,
    renterRequisiteData,
    businessProfileId,
    linkModalDisclosure,
    handleError,
  ]);

  const handleEdit = useCallback(() => {
    if (createNewRequisite && renterRequisiteData) {
      setActiveStep(2); // Редактирование реквизитов
    } else if (officesData) {
      setActiveStep(1); // Редактирование офисов
    } else {
      setActiveStep(0); // Редактирование данных приглашения
    }
  }, [createNewRequisite, renterRequisiteData, officesData]);

  const changeStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  useEffect(() => {
    if (linkModalDisclosure.isOpen) {
      wasModalOpenRef.current = true;
    } else if (wasModalOpenRef.current) {
      // Модалка была открыта и теперь закрыта - делаем редирект
      navigate("/dashboard/invitations");
    }
  }, [linkModalDisclosure.isOpen, navigate]);

  return (
    <>
      <InvitationLinkModal
        disclosure={linkModalDisclosure}
        invitationLink={invitationLink}
      />

      <h1 className="text-xl font-bold mb-4 mt-2">Создание приглашения</h1>

      <div className="flex flex-col lg:flex-row gap-4 w-full">
        <div className="lg:w-1/3">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            changeStep={changeStep}
            colorScheme="primary"
          />
        </div>

        <div className="lg:w-2/3">
          <div className="w-full h-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
            <div className="flex flex-col gap-4">
              {!(
                "hideTitle" in steps[activeStep] && steps[activeStep].hideTitle
              ) && (
                <div className="flex flex-col gap-2">
                  <span className="text-lg font-semibold">
                    {steps[activeStep].name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {steps[activeStep].longDescription}
                  </span>
                </div>
              )}

              {activeStep === 0 && (
                <InvitationForm
                  type="existing_renter"
                  defaultValues={invitationData || undefined}
                  onSubmit={handleInvitationSubmit}
                  modalErrors={modalErrors}
                  createNewRequisite={createNewRequisite}
                  onCreateRequisiteToggle={handleCreateRequisiteToggle}
                />
              )}

              {activeStep === 1 && (
                <OfficesSelectionForm
                  defaultValues={officesData || undefined}
                  onSubmit={handleOfficesSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 2 && createNewRequisite && (
                <RenterRequisiteForm
                  defaultValues={renterRequisiteData || undefined}
                  onSubmit={handleRenterRequisiteSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === steps.length - 1 && (
                <InvitationReview
                  invitationData={invitationData || ({} as InvitationFormData)}
                  renterRequisiteData={renterRequisiteData || undefined}
                  officesData={officesData || undefined}
                  onEdit={handleEdit}
                  onSubmit={handleReviewSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
